import { useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Quiz de 3 passos que cruza o que o visitante busca (interesse × esforço × onde
 * está na ilha) com o perfil de cada ponto turístico e recomenda 1 a 3 lugares.
 * Eixos definidos a partir das dúvidas reais das SERPs (radar de conteúdo):
 * história × natureza × vista/foto × golfinhos; trilha leve × encarar subida;
 * Nova Brasília (norte) × Encantadas (sul). 100% client-side.
 * Parque Estadual e Estação Ecológica ficam fora (contexto, não recomendação).
 */

type Answers = { interesse?: string; esforco?: string; local?: string };

// Perfil de cada ponto visitável para o quiz
const TAGS: Record<string, { interesse: string[]; esforco: 'leve' | 'medio' | 'alto'; vila: string }> = {
  'farol-das-conchas': { interesse: ['historia', 'vista'], esforco: 'alto', vila: 'nova-brasilia' },
  'fortaleza-nossa-senhora-dos-prazeres': { interesse: ['historia'], esforco: 'medio', vila: 'nova-brasilia' },
  'capela-sao-francisco-de-assis': { interesse: ['historia', 'vista'], esforco: 'medio', vila: 'encantadas' },
  'gruta-das-encantadas': { interesse: ['natureza'], esforco: 'leve', vila: 'encantadas' },
  'bacia-da-sereia': { interesse: ['natureza'], esforco: 'leve', vila: 'encantadas' },
  'istmo-passa-passa': { interesse: ['natureza', 'vista'], esforco: 'leve', vila: 'nova-brasilia' },
  'ponta-oeste': { interesse: ['natureza'], esforco: 'alto', vila: 'nova-brasilia' },
  'baia-dos-golfinhos': { interesse: ['golfinhos'], esforco: 'leve', vila: 'encantadas' },
};

const STEPS = [
  {
    key: 'interesse' as const,
    title: 'O que mais te atrai?',
    options: [
      { v: 'historia', label: 'História e monumentos', icon: '🏛️' },
      { v: 'natureza', label: 'Natureza e formações', icon: '🌿' },
      { v: 'vista', label: 'A melhor vista pra foto', icon: '📸' },
      { v: 'golfinhos', label: 'Ver golfinhos e vida marinha', icon: '🐬' },
    ],
  },
  {
    key: 'esforco' as const,
    title: 'Disposição para caminhar?',
    options: [
      { v: 'leve', label: 'Quero algo fácil e rápido', icon: '🚶' },
      { v: 'topo', label: 'Topo encarar trilha/escadaria pela recompensa', icon: '🥾' },
    ],
  },
  {
    key: 'local' as const,
    title: 'Onde você vai estar na ilha?',
    options: [
      { v: 'tanto-faz', label: 'Ainda não sei / tanto faz', icon: '🧭' },
      { v: 'nova-brasilia', label: 'Nova Brasília / Farol (norte)', icon: '🗼' },
      { v: 'encantadas', label: 'Encantadas (sul)', icon: '🏖️' },
    ],
  },
];

const INTERESSE_LABEL: Record<string, string> = {
  historia: 'História',
  natureza: 'Natureza',
  vista: 'Vista panorâmica',
  golfinhos: 'Vida marinha',
};

function score(handle: string, a: Answers): number {
  const t = TAGS[handle];
  if (!t) return -Infinity;
  let s = 0;
  // 1) interesse
  if (a.interesse && t.interesse.includes(a.interesse)) s += 4;
  // 2) esforço
  if (a.esforco === 'leve') s += t.esforco === 'leve' ? 3 : t.esforco === 'medio' ? 1 : -2;
  if (a.esforco === 'topo') s += t.esforco === 'alto' ? 3 : t.esforco === 'medio' ? 1.5 : 0.5;
  // 3) onde está (proximidade)
  if (a.local && a.local !== 'tanto-faz') s += t.vila === a.local ? 3 : 0;
  return s;
}

function reasons(handle: string, a: Answers, vilarejoName: string): string[] {
  const t = TAGS[handle];
  const out: string[] = [];
  if (a.interesse && t?.interesse.includes(a.interesse)) out.push(INTERESSE_LABEL[a.interesse]);
  else if (t?.interesse[0]) out.push(INTERESSE_LABEL[t.interesse[0]]);
  if (a.esforco === 'leve' && t?.esforco === 'leve') out.push('Acesso fácil');
  if (a.esforco === 'topo' && t?.esforco === 'alto') out.push('Vale o esforço');
  if (a.local && a.local !== 'tanto-faz' && t?.vila === a.local) out.push('Pertinho de você');
  out.push(vilarejoName);
  return [...new Set(out.filter(Boolean))].slice(0, 3);
}

export function PontosTuristicosQuiz({ atracoes }: { atracoes: any[] }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [done, setDone] = useState(false);

  const pick = (key: string, v: string) => {
    const next = { ...answers, [key]: v };
    setAnswers(next);
    if (step < STEPS.length - 1) setStep(step + 1);
    else setDone(true);
  };
  const reset = () => { setAnswers({}); setStep(0); setDone(false); };

  let results: any[] = [];
  if (done) {
    const ranked = atracoes
      .filter(p => TAGS[p.handle])
      .map(p => ({ p, s: score(p.handle, answers) }))
      .sort((a, b) => b.s - a.s);
    const top = ranked[0]?.s ?? 0;
    results = ranked.filter(r => r.s >= top - 1.5).slice(0, 3).map(r => r.p);
  }

  return (
    <section className="mb-16 bg-teal-deep -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-12 rounded-2xl" id="quiz">
      <div className="max-w-3xl mx-auto text-center">
        <span className="font-body text-sm text-sand-200 font-semibold uppercase tracking-wider">Teste rápido</span>
        <h2 className="font-display text-3xl sm:text-4xl text-white mt-1 mb-2">Que ponto turístico combina com você?</h2>
        <p className="font-body text-sand-100/90 mb-8">Responda 3 perguntas e eu indico o que priorizar na sua visita.</p>

        {!done ? (
          <div className="bg-white rounded-2xl p-6 sm:p-8 text-left shadow-lg">
            <div className="flex items-center gap-2 mb-5">
              {STEPS.map((_, i) => (
                <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? 'bg-coral' : 'bg-sand-200'}`} />
              ))}
            </div>
            <p className="font-body text-xs text-warm-gray mb-1">Passo {step + 1} de {STEPS.length}</p>
            <h3 className="font-display text-xl sm:text-2xl text-warm-dark mb-5">{STEPS[step].title}</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {STEPS[step].options.map(opt => (
                <button
                  key={opt.v}
                  onClick={() => pick(STEPS[step].key, opt.v)}
                  className="flex items-center gap-3 p-4 rounded-xl border border-sand-200 hover:border-coral hover:bg-coral/5 transition-all text-left"
                >
                  <span className="text-2xl flex-shrink-0">{opt.icon}</span>
                  <span className="font-body font-medium text-warm-dark">{opt.label}</span>
                </button>
              ))}
            </div>
            {step > 0 && (
              <button onClick={() => setStep(step - 1)} className="mt-5 font-body text-sm text-warm-gray hover:text-warm-dark">
                ← Voltar
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-6 sm:p-8 text-left shadow-lg">
            <p className="font-body text-sm text-coral font-semibold uppercase tracking-wider mb-1">
              {results.length > 1 ? 'Suas melhores opções' : 'O ponto com a sua cara'}
            </p>
            <h3 className="font-display text-2xl text-warm-dark mb-5">
              Encontrei {results.length === 1 ? 'o lugar certo' : `${results.length} lugares`} pra você
            </h3>
            <div className="space-y-4">
              {results.map((p, i) => (
                <div key={p.handle} className={`flex gap-4 p-3 rounded-xl border ${i === 0 ? 'border-coral/40 bg-coral/5' : 'border-sand-200'}`}>
                  <Link to={`/ponto-turistico/${p.handle}`} className="block w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover" loading="lazy" decoding="async" width={96} height={96} />
                  </Link>
                  <div className="flex-1 min-w-0">
                    {i === 0 && <span className="font-body text-[11px] font-bold text-coral uppercase tracking-wide">★ Melhor combinação</span>}
                    <h4 className="font-display text-lg text-warm-dark leading-tight">{p.title}</h4>
                    <div className="flex flex-wrap gap-1.5 my-1.5">
                      {reasons(p.handle, answers, p.vilarejoName).map((r, j) => (
                        <span key={j} className="font-body text-[11px] bg-sand-100 text-warm-dark px-2 py-0.5 rounded-full">{r}</span>
                      ))}
                    </div>
                    <Link to={`/ponto-turistico/${p.handle}`} className="font-body text-sm font-semibold text-teal-deep hover:underline">
                      Ver detalhes do ponto turístico →
                    </Link>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={reset} className="mt-6 font-body text-sm text-warm-gray hover:text-warm-dark">↻ Refazer o teste</button>
          </div>
        )}
      </div>
    </section>
  );
}
